import { useMusic } from "@/contexts/MusicContext";
import { useMusicPlayer } from "./player/useMusicPlayer";
import { PlayerContainer } from "./player/PlayerContainer";
import { PlayerCollapsedView } from "./player/PlayerCollapsedView";
import { PlayerExpandedView } from "./player/PlayerExpandedView";

interface MusicPlayerProps {
  mobileBottomOffsetClass?: string;
  tabletBottomOffsetClass?: string;
  desktopSidebarOffsetClass?: string;
}

export default function MusicPlayer({
  mobileBottomOffsetClass,
  tabletBottomOffsetClass,
  desktopSidebarOffsetClass,
}: MusicPlayerProps) {
  const { isPlayerExpanded, setIsPlayerExpanded } = useMusic();

  const {
    currentTrack,
    isPlaying,
    isActuallyPlaying,
    setIsActuallyPlaying,
    currentTime,
    volume,
    liked,
    audioRef,
    sponsorData,
    handleTimeUpdate,
    handleSeek,
    handleVolumeChange,
    toggleLike,
    formatTime,
    handleStreamComplete,
    setIsPlaying,
  } = useMusicPlayer();

  if (!currentTrack) {
    return null;
  }

  const handleSeekToTime = (time: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  };

  // Convert array-based seek to single value seek
  const handleSeekFromSlider = (value: number[]) => {
    handleSeek(value);
  };

  const toggleExpand = () => {
    setIsPlayerExpanded(!isPlayerExpanded);
  };

  const handleCollapse = () => {
    setIsPlayerExpanded(false);
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  return (
    <PlayerContainer
      isExpanded={isPlayerExpanded}
      mobileBottomOffsetClass={mobileBottomOffsetClass}
      tabletBottomOffsetClass={tabletBottomOffsetClass}
      desktopSidebarOffsetClass={desktopSidebarOffsetClass}
      currentTrack={currentTrack}
      isPlaying={isPlaying}
      audioRef={audioRef}
      isActuallyPlaying={isActuallyPlaying}
      setIsActuallyPlaying={setIsActuallyPlaying}
      onStreamComplete={handleStreamComplete}
      onTimeUpdate={handleTimeUpdate}
      setIsPlaying={setIsPlaying}
      onCollapse={handleCollapse}
    >
      {/* Collapsed View */}
      {!isPlayerExpanded && (
        <PlayerCollapsedView
          currentTrack={currentTrack}
          isPlaying={isPlaying}
          liked={liked}
          sponsorName={sponsorData?.display_name}
          sponsorUsername={
            sponsorData?.sponsorUsername || sponsorData?.username
          }
          volume={volume}
          currentTime={currentTime}
          audioRef={audioRef}
          onToggleLike={toggleLike}
          onTogglePlayPause={togglePlayPause}
          onVolumeChange={handleVolumeChange}
          onSeek={handleSeekFromSlider}
          onSeekToTime={handleSeekToTime}
          onToggleExpand={toggleExpand}
          formatTime={formatTime}
          setIsPlaying={setIsPlaying}
        />
      )}

      {/* Expanded View */}
      {isPlayerExpanded && (
        <PlayerExpandedView
          currentTrack={currentTrack}
          isPlaying={isPlaying}
          liked={liked}
          sponsorName={sponsorData?.display_name}
          sponsorUsername={
            sponsorData?.sponsorUsername || sponsorData?.username
          }
          sponsorAvatar={sponsorData?.avatar_url}
          volume={volume}
          currentTime={currentTime}
          audioRef={audioRef}
          onToggleLike={toggleLike}
          onTogglePlayPause={togglePlayPause}
          onVolumeChange={handleVolumeChange}
          onSeek={handleSeekFromSlider}
          onSeekToTime={handleSeekToTime}
          onToggleExpand={toggleExpand}
          formatTime={formatTime}
          setIsPlaying={setIsPlaying}
        />
      )}
    </PlayerContainer>
  );
}
